import { takeLatest, put, all, call } from 'redux-saga/effects';
import * as commonActions from '@store/common/actions';
import { DASHBOARD_TYPE, DATE_SHORTCUTS, DEFAULT_METRIC, NOC_DRILLDOWN } from '@utils/constants';
import { setComparisonInterval } from '@store/summaryContainer/comparison/actions';
import { selectedMetricChart, setMetrics } from '@store/metricsChartContainer/actions';
import { updateSelectedMetrics, setDataType } from '@store/viewerSessions/actions';
import { RESET_DRILL_DOWN } from '@store/summaryContainer/drilldown/types';
import { transformOldMetrics } from '@components/viewer-sessions/utils';
import { setPersistedData } from '@utils/persistenceUtil';
import { resetAiAlertState, setSelectedAiAlert } from '@store/trends/aiAlerts/actions';
import { changeMode } from '@store/trends/actions';
import {
  MODE_AI_ALERT_DIAGNOSTICS,
  MODE_TRENDS,
  MODE_ALERT_DIAGNOSTICS,
} from '@store/trends/constants';
import { setSummaryWidgets } from '@store/summaryWidgets/actions';
import { addNewSharedTab } from '../../sharedTabs/action';
import { injectSharedState as birdChartInjectSharedState } from '../birdChart/actions';
import { setSharingState as setAlertsSharingState } from '../alerts/actions';
import * as trendsBaseActions from '../base/actions';
import { get as lodashGet } from 'lodash';
import * as types from './types';
import { DEFAULT_FILTER_COLOR } from '@utils/colorPalette';
import { calcShortcutInterval } from '@utils';
import getGranularity from '@utils/granularity';

export function* updateDrilldownState(state) {
  const { common = {}, summaryWidgets = {}, metricsChartContainer = {}, summary } = state;

  const moduleTrendsState = state['module:trends'];
  const { drilldownData = [] } = summary || {};
  const metric = metricsChartContainer.metric || DEFAULT_METRIC;

  yield put(changeMode(NOC_DRILLDOWN));

  if (moduleTrendsState) {
    const { base = {} } = moduleTrendsState;
    yield put(trendsBaseActions.setSharedState(base));
  }

  const shortcut = DATE_SHORTCUTS.last6Hours;
  const [from, to] = calcShortcutInterval(DATE_SHORTCUTS.last6Hours);
  const defaultInterval = {
    from: from.format(),
    to: to.format(),
    shortcut,
    granularity: getGranularity(from, to, true, false, shortcut),
  };

  // add/overwrite interval
  const updatedCommonState = {
    ...common,
    interval: common.interval ?? defaultInterval,
  };

  yield all([
    put(commonActions.setSharingState(updatedCommonState)),
    put({
      type: RESET_DRILL_DOWN,
      drilldownData,
    }),
  ]);

  const chartMetrics = metricsChartContainer.metrics.length
    ? metricsChartContainer.metrics
    : [metric];
  yield setPersistedData(setMetrics(chartMetrics));
  yield setPersistedData(selectedMetricChart(metric, null));
  yield setPersistedData(setSummaryWidgets(summaryWidgets));
}

export function* updateState(state) {
  const {
    common,
    comparison,
    summaryWidgets = {},
    metricsChartContainer = {},
    viewerDrillDownBirdChart,
    viewerSessionsTable,
    summary,
    selectedAiAlert,
  } = state;

  const moduleTrendsState = state['module:trends'];

  const { drilldownData = [] } = summary || {};
  const metric = metricsChartContainer.metric;

  // simulates persisted action to avoid triggering persistence
  // as event types are same

  // Since some sagas are watching the action, firstly set the metric.
  yield setPersistedData(selectedMetricChart(metric));

  // new layout store actions
  yield setPersistedData(setSummaryWidgets(summaryWidgets));

  const chartMetrics = metricsChartContainer.metrics.length
    ? metricsChartContainer.metrics
    : [metric];
  yield setPersistedData(setMetrics(chartMetrics));

  if (moduleTrendsState) {
    if ('alerts' in moduleTrendsState) {
      yield put(setAlertsSharingState(moduleTrendsState.alerts));
    }

    // new layout
    const { base = {} } = moduleTrendsState;
    yield put(trendsBaseActions.setSharedState(base));
  }

  if (selectedAiAlert) {
    yield put(setSelectedAiAlert({ selectedAiAlert }));
  } else {
    yield put(resetAiAlertState());
  }

  yield all([
    put(commonActions.setSharingState({ ...common, filterColor: DEFAULT_FILTER_COLOR })),
    put({
      type: RESET_DRILL_DOWN,
      drilldownData,
    }),
  ]);

  if (comparison) {
    yield put(setComparisonInterval(comparison.interval.from, comparison.interval.to));
  }

  if (viewerDrillDownBirdChart) {
    yield put(birdChartInjectSharedState(viewerDrillDownBirdChart));
    yield put(
      updateSelectedMetrics(transformOldMetrics(viewerSessionsTable.selectedMetrics), 'trends'),
    );
    if (viewerSessionsTable && viewerSessionsTable.dataType) {
      yield put(setDataType(viewerSessionsTable.dataType, 'trends'));
    }
  }
}

function* afterRequest({ payload: sharedData }) {
  const rawState: any = lodashGet(sharedData, 'state', null);
  const dashboard: any = lodashGet(sharedData, 'dashboard', null);
  const state = JSON.parse(rawState);

  if (!state) {
    return;
  }

  const dashboardType = state.selectedAiAlert
    ? DASHBOARD_TYPE.AI_ALERT_DIAGNOSTICS
    : dashboard === 'app-experience'
      ? DASHBOARD_TYPE.APP_EXPERIENCE
      : DASHBOARD_TYPE.TRENDS;

  // workaround as shared manual alerts page does not have a unique type identifier
  const selectedManualAlert = lodashGet(state, ['module:trends', 'alerts', 'selectedEvent'], null);

  try {
    const {
      common: { selectedCustomerId },
    } = state;
    yield call(updateState, state);
    yield put(
      addNewSharedTab({ id: sharedData.id, state, type: dashboardType }, selectedCustomerId),
    );
    if (dashboardType === DASHBOARD_TYPE.AI_ALERT_DIAGNOSTICS) {
      yield put(changeMode(MODE_AI_ALERT_DIAGNOSTICS));
    } else {
      yield put(changeMode(selectedManualAlert ? MODE_ALERT_DIAGNOSTICS : MODE_TRENDS));
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}

function* watchSetSharing() {
  yield takeLatest([types.SET_SHARING], afterRequest);
}

export default [watchSetSharing];
